/**
 * PackageLibrary — grid of form-package cards.
 *
 * A package bundles multiple forms into ONE patient link. Mirrors FormLibrary
 * conventions (card grid, refreshSignal-keyed load, Edit/Delete actions).
 *
 * Features:
 *   - Card shows name / description / N forms / Public|Private badge
 *   - Public URL row (copy + open) → ${origin}/pkg/${locationId}/${slug}
 *   - Edit → opens PackageBuilder
 *   - Delete (soft) with confirmation
 *   - "New Package" button
 */

import React, { useEffect, useState, useCallback } from 'react';
import { packages as packagesApi, PackageSummary } from '../../../lib/api.js';
import { useAuth } from '../../../lib/auth-context.js';

interface Props {
  onEdit:         (pkg: PackageSummary) => void;
  onNewPackage:   () => void;
  refreshSignal?: number;
}

export default function PackageLibrary({ onEdit, onNewPackage, refreshSignal }: Props): React.ReactElement {
  const { user } = useAuth();
  const locationId = user?.role === 'staff' ? user.locationId : '';

  const [pkgs,     setPkgs]     = useState<PackageSummary[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await packagesApi.list();
      if (res.success && res.data) setPkgs(res.data);
      else setError(res.error ?? 'Failed to load packages');
    } catch {
      setError('Failed to load packages');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load, refreshSignal]);

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Delete "${name}"? This cannot be undone.`)) return;
    setDeleting(id);
    try {
      await packagesApi.remove(id);
      setPkgs((prev) => prev.filter((p) => p.id !== id));
    } catch {
      alert('Failed to delete package');
    } finally {
      setDeleting(null);
    }
  };

  const copyPublicUrl = (slug: string) => {
    const url = `${window.location.origin}/pkg/${locationId}/${slug}`;
    navigator.clipboard.writeText(url).then(() => alert('Public URL copied!'));
  };

  if (loading) return <div style={styles.center}><p style={styles.muted}>Loading packages…</p></div>;
  if (error)   return <div style={styles.center}><p style={{ color: '#c0392b' }}>{error}</p></div>;

  return (
    <div style={styles.container}>
      <div style={styles.toolbar}>
        <h3 style={styles.heading}>Form Packages</h3>
        <button style={styles.newBtn} onClick={onNewPackage}>+ New Package</button>
      </div>

      {pkgs.length === 0 ? (
        <div style={styles.empty}>
          <span style={{ fontSize: 40 }}>📦</span>
          <p style={styles.muted}>No packages yet. Bundle a few forms into one patient link.</p>
          <button style={styles.newBtn} onClick={onNewPackage}>+ New Package</button>
        </div>
      ) : (
        <div style={styles.grid}>
          {pkgs.map((p) => (
            <div key={p.id} style={styles.card}>
              {/* Header */}
              <div style={styles.cardHeader}>
                <span style={styles.cardName}>{p.name}</span>
                {p.isPublic ? (
                  <span style={styles.publicBadge}>🌐 Public</span>
                ) : (
                  <span style={styles.privateBadge}>🔒 Private</span>
                )}
              </div>

              {/* Meta */}
              {p.description && <p style={styles.desc}>{p.description}</p>}
              <div style={styles.cardMeta}>
                <span style={styles.fieldCount}>
                  {p.itemCount} {p.itemCount === 1 ? 'form' : 'forms'}
                </span>
              </div>
              {p.formNames.length > 0 && (
                <p style={styles.formList}>{p.formNames.join(' · ')}</p>
              )}

              {/* Public URL */}
              {p.isPublic && p.publicSlug && (
                <div style={styles.publicUrlRow}>
                  <button
                    style={styles.copyUrlBtn}
                    onClick={() => copyPublicUrl(p.publicSlug!)}
                    title="Copy public package URL"
                  >
                    📋 Copy URL
                  </button>
                  <a
                    style={styles.openUrlBtn}
                    href={`/pkg/${locationId}/${p.publicSlug}`}
                    target="_blank"
                    rel="noreferrer"
                    title="Open package in new tab"
                  >
                    ↗ Open
                  </a>
                </div>
              )}

              {/* Actions */}
              <div style={styles.cardActions}>
                <button style={styles.editBtn} onClick={() => onEdit(p)}>Edit</button>
                <button
                  style={styles.deleteBtn}
                  onClick={() => handleDelete(p.id, p.name)}
                  disabled={deleting === p.id}
                >
                  {deleting === p.id ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { padding: '16px 20px', height: '100%', overflowY: 'auto' },
  toolbar:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  heading:   { margin: 0, fontSize: 16, fontWeight: 600, color: '#111827' },
  newBtn:    { background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 500 },
  center:    { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12 },
  muted:     { color: '#9ca3af', fontSize: 14, margin: 0 },
  empty:     { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12 },
  grid:      { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 },
  card:      { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8 },
  cardHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 },
  cardName:  { fontWeight: 600, fontSize: 14, color: '#111827', flex: 1 },
  desc:      { margin: 0, fontSize: 12, color: '#6b7280', lineHeight: 1.5 },
  cardMeta:  { display: 'flex', alignItems: 'center', gap: 8 },
  fieldCount: { fontSize: 12, color: '#9ca3af' },
  formList:  { margin: 0, fontSize: 11, color: '#9ca3af', lineHeight: 1.5 },
  publicBadge:  { fontSize: 11, background: '#ecfdf5', color: '#059669', padding: '1px 6px', borderRadius: 4, flexShrink: 0 },
  privateBadge: { fontSize: 11, background: '#f3f4f6', color: '#6b7280', padding: '1px 6px', borderRadius: 4, flexShrink: 0 },
  publicUrlRow: { display: 'flex', gap: 6 },
  copyUrlBtn: { background: 'none', border: '1px dashed #d1d5db', borderRadius: 4, padding: '3px 8px', cursor: 'pointer', fontSize: 11, color: '#6b7280' },
  openUrlBtn: { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 4, padding: '3px 8px', fontSize: 11, color: '#059669', fontWeight: 500, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' },
  cardActions: { display: 'flex', gap: 6, marginTop: 4 },
  editBtn:     { flex: 1, padding: '5px 10px', border: '1px solid #d1d5db', borderRadius: 5, background: '#f9fafb', cursor: 'pointer', fontSize: 12, color: '#374151' },
  deleteBtn:   { padding: '5px 10px', border: '1px solid #fca5a5', borderRadius: 5, background: '#fef2f2', cursor: 'pointer', fontSize: 12, color: '#b91c1c' },
};
